import { downloadGeoJSON, featureCollection } from '../../common/geojson';
import { areaFeature, baselineFeature, coastlineFeatures, transectFeature } from '../utils/coastlineGeoJson';

/**
 * @constant GEOJSON_MODULE
 * @description Module key, used to name the downloaded file.
 * @memberof module:ShoreR
 */
const GEOJSON_MODULE = 'shore_r';

/**
 * @function buildGeoJsonExport
 * @description Gathers a finished Shore-R analysis into one FeatureCollection: the area of
 * interest, the baseline, the transects with their rates - smoothed ones included, since the
 * transects are copied whole - and the extracted coastlines.
 * The projected future coastlines are left to Shore-R's own export dialog, which already writes
 * them with the horizons and labels they need; this icon covers what the analysis measured.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring, `[lng, lat]` pairs.
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices.
 * @param {Object} [params.shoreRData] - `state.results.shoreRData`, holding `transectData` and
 *  `shorelinesGeoJSON`.
 * **Called by:
 * @see {ProcessingPage} - Shore-R's result page, through the GIS export icon.
 * @memberof module:ShoreR
 * @returns {Object} A FeatureCollection, empty when the analysis has produced nothing yet.
 */
export const buildGeoJsonExport = ({ coordinates, baselineCoords, shoreRData }) =>
  featureCollection([
    areaFeature(coordinates),
    baselineFeature(baselineCoords),
    (shoreRData?.transectData || []).map(transectFeature),
    coastlineFeatures(shoreRData?.shorelinesGeoJSON),
  ]);

/**
 * @function exportGeoJson
 * @description Builds the collection and hands it to the browser.
 * @param {Object} params - Same as {@link buildGeoJsonExport}.
 * **Called by:
 * @see {ProcessingPage} - Shore-R's result page.
 * @memberof module:ShoreR
 * @returns {Boolean} False when there was nothing to write.
 */
export const exportGeoJson = (params) => downloadGeoJSON(buildGeoJsonExport(params), GEOJSON_MODULE);
